import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import ProjectBoardTab from '../components/ProjectBoardTab';
import ProjectCalendarTab from '../components/ProjectCalendarTab';
import ProjectListTab from '../components/ProjectListTab';
import ProjectChecklistsTab from '../components/ProjectChecklistsTab';
import ProjectRolesTab from '../components/ProjectRolesTab';
import EventNotesTab from '../components/EventNotesTab';
import EventIntakeFormTab from '../components/EventIntakeFormTab';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import { useAuth } from '../context/AuthContext';
import { fetchEvent, updateEvent, type EventRow } from '../api/events';
import { fetchVerifiedUsers } from '../api/users';
import { formatTaskDate } from '../utils/formatTaskDate';
import './Page.css';
import './ClientDetail.css';
import './ProjectDetail.css';
import './Events.css';

const TABS = ['Overview', 'Board', 'List', 'Calendar', 'Intake Form', 'Checklists', 'Roles', 'Notes'] as const;
type TabId = (typeof TABS)[number];

function hasTfriRole(user: { roles?: string[] } | null | undefined): boolean {
  const roles = user?.roles;
  return Array.isArray(roles) && roles.includes('admin');
}

function formatDateTime(date: string | null, time: string | null): string {
  if (!date) return '—';
  const d = formatTaskDate(date);
  return time ? `${d} · ${time}` : d;
}

const EventDetail: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const idNum = eventId ? parseInt(eventId, 10) : NaN;

  const [searchParams, setSearchParams] = useSearchParams();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const initialTab = ((): TabId => {
    const t = searchParams.get('tab');
    return TABS.includes(t as TabId) ? (t as TabId) : 'Overview';
  })();
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const selectTab = useCallback(
    (tab: TabId) => {
      setActiveTab(tab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'Overview') next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editCollabIds, setEditCollabIds] = useState<number[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [verifiedUsers, setVerifiedUsers] = useState<{ id: number; username: string }[]>([]);
  const [addCollabOpen, setAddCollabOpen] = useState(false);
  const [addCollabIds, setAddCollabIds] = useState<number[]>([]);
  const [addCollabSaving, setAddCollabSaving] = useState(false);
  const [addCollabError, setAddCollabError] = useState('');

  const tfri = hasTfriRole(user);
  const canEdit =
    !!user?.id &&
    !!event &&
    (tfri ||
      event.created_by_user_id === user.id ||
      event.collaborators.some((c) => c.user_id === user.id));
  const isVoid = event?.status === 'Closed';

  const collaboratorPickerUsers = useMemo(() => {
    const ownerId = event?.created_by_user_id;
    if (ownerId == null) return verifiedUsers;
    return verifiedUsers.filter((u) => u.id !== ownerId);
  }, [verifiedUsers, event?.created_by_user_id]);

  const loadEvent = useCallback(async () => {
    if (!user?.id || !Number.isInteger(idNum) || idNum < 1) return;
    setLoading(true);
    setError('');
    try {
      const ev = await fetchEvent(idNum, user.id);
      setEvent(ev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load event.');
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, idNum]);

  useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list.map((u) => ({ id: u.id, username: u.username })));
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openEdit = () => {
    if (!event) return;
    setEditName(event.name);
    setEditLocation(event.location ?? '');
    setEditStartDate(event.start_date ?? '');
    setEditEndDate(event.end_date ?? '');
    setEditStartTime(event.start_time ?? '');
    setEditEndTime(event.end_time ?? '');
    setEditCollabIds(event.collaborators.map((c) => c.user_id));
    setEditError('');
    setEditOpen(true);
  };

  const openAddCollab = () => {
    setAddCollabIds([]);
    setAddCollabError('');
    setAddCollabOpen(true);
  };

  const submitAddCollab = async () => {
    if (!user?.id || !event) return;
    const existing = event.collaborators.map((c) => c.user_id);
    const merged = Array.from(new Set([...existing, ...addCollabIds]));
    setAddCollabSaving(true);
    setAddCollabError('');
    try {
      const updated = await updateEvent(event.id, { user_id: user.id, collaborator_user_ids: merged });
      setEvent(updated);
      setAddCollabOpen(false);
      setAddCollabIds([]);
    } catch (e) {
      setAddCollabError(e instanceof Error ? e.message : 'Failed to add collaborators.');
    } finally {
      setAddCollabSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!user?.id || !event) return;
    const name = editName.trim();
    if (!name) {
      setEditError('Event name is required.');
      return;
    }
    if (editStartDate && editEndDate && editEndDate < editStartDate) {
      setEditError('End date cannot be before the start date.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const updated = await updateEvent(event.id, {
        user_id: user.id,
        name,
        location: editLocation.trim() || null,
        start_date: editStartDate || null,
        end_date: editEndDate || null,
        start_time: editStartTime || null,
        end_time: editEndTime || null,
        collaborator_user_ids: editCollabIds,
      });
      setEvent(updated);
      setEditOpen(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setEditSaving(false);
    }
  };

  const submitToggleStatus = async () => {
    if (!user?.id || !event) return;
    const nextStatus = event.status === 'Closed' ? 'Active' : 'Closed';
    setStatusSaving(true);
    setEditError('');
    try {
      const updated = await updateEvent(event.id, { user_id: user.id, status: nextStatus });
      setEvent(updated);
      setEditOpen(false);
    } catch (e) {
      setEditError(
        e instanceof Error ? e.message : `Failed to ${nextStatus === 'Closed' ? 'close' : 'reopen'} event.`
      );
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <div className="project-detail-page-top">
            <button type="button" className="project-detail-back" onClick={() => navigate('/events')}>
              ← Events
            </button>
          </div>
          <div className="page-content project-detail-content">
            {!user ? (
              <p className="page-subtitle">Log in to view this event.</p>
            ) : !Number.isInteger(idNum) || idNum < 1 ? (
              <p className="create-order-error" role="alert">
                Invalid event.
              </p>
            ) : loading ? (
              <p className="page-subtitle">Loading…</p>
            ) : error || !event ? (
              <p className="create-order-error" role="alert">
                {error || 'Event not found.'}
              </p>
            ) : (
              <>
                <h1 className="project-detail-title">
                  {event.name}
                  {isVoid ? <span className="events-void-badge">Void</span> : null}
                </h1>

                <div className="project-detail-toolbar">
                  <div className="project-detail-tabs" role="tablist" aria-label="Event views">
                    {TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab}
                        className={`project-detail-tab${activeTab === tab ? ' project-detail-tab--active' : ''}`}
                        onClick={() => selectTab(tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  {canEdit ? (
                    <div className="project-detail-actions">
                      <button type="button" className="project-detail-secondary-btn" onClick={openAddCollab}>
                        Add collaborators
                      </button>
                      <button type="button" className="project-detail-primary-btn" onClick={openEdit}>
                        Edit event
                      </button>
                    </div>
                  ) : null}
                </div>

                {isVoid ? (
                  <div className="events-void-banner" role="status">
                    This event is closed and listed under Void Events. The intake form no longer accepts submissions.
                  </div>
                ) : null}

                <div className="project-detail-panel" role="tabpanel" aria-label={activeTab}>
                  {activeTab === 'Overview' ? (
                    <div className="client-info-card">
                      <div className="client-info-header">
                        <h3 className="section-title">Event information</h3>
                      </div>
                      <div className="client-info-grid">
                        <div className="client-info-item client-info-item--full">
                          <label className="client-info-label">Location</label>
                          <div className="client-info-value">{event.location || '—'}</div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">Start</label>
                          <div className="client-info-value">{formatDateTime(event.start_date, event.start_time)}</div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">End</label>
                          <div className="client-info-value">{formatDateTime(event.end_date, event.end_time)}</div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">Status</label>
                          <div className="client-info-value">{isVoid ? 'Void (closed)' : 'Active'}</div>
                        </div>
                        <div className="client-info-item">
                          <label className="client-info-label">Created by</label>
                          <div className="client-info-value">{event.created_by_username ?? '—'}</div>
                        </div>
                        <div className="client-info-item client-info-item--full">
                          <label className="client-info-label">Collaborators</label>
                          <div className="client-info-value">
                            {event.collaborators.length === 0
                              ? 'None'
                              : event.collaborators.map((c) => c.username).join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : activeTab === 'Board' ? (
                    <ProjectBoardTab projectId={event.project_id} eventId={event.id} viewerUserId={user.id} canEditBoard={canEdit} />
                  ) : activeTab === 'List' ? (
                    <ProjectListTab projectId={event.project_id} eventId={event.id} viewerUserId={user.id} canRemoveFromProject={canEdit} />
                  ) : activeTab === 'Calendar' ? (
                    <ProjectCalendarTab projectId={event.project_id} eventId={event.id} viewerUserId={user.id} />
                  ) : activeTab === 'Intake Form' ? (
                    <EventIntakeFormTab eventId={event.id} viewerUserId={user.id} canEdit={canEdit} isVoid={isVoid} />
                  ) : activeTab === 'Checklists' ? (
                    <ProjectChecklistsTab projectId={event.project_id} viewerUserId={user.id} canEdit={canEdit} />
                  ) : activeTab === 'Roles' ? (
                    <ProjectRolesTab projectId={event.project_id} viewerUserId={user.id} canEdit={canEdit} />
                  ) : activeTab === 'Notes' ? (
                    <EventNotesTab eventId={event.id} viewerUserId={user.id} />
                  ) : (
                    <p className="project-detail-panel-placeholder">Content for {activeTab} will go here.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <Modal isOpen={addCollabOpen} onClose={() => setAddCollabOpen(false)} title="Add collaborators" wide>
        <div className="modal-body">
          <p className="page-subtitle" style={{ marginTop: 0 }}>
            Search and add verified users. Existing collaborators stay on the event.
          </p>
          <TaskAssigneePicker
            users={collaboratorPickerUsers}
            value={addCollabIds}
            onChange={setAddCollabIds}
            addPlaceholder="Search user to add…"
          />
          {addCollabError ? (
            <p className="create-order-error" role="alert">
              {addCollabError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addCollabSaving} onClick={() => setAddCollabOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addCollabSaving} onClick={() => void submitAddCollab()}>
            {addCollabSaving ? 'Saving…' : 'Add to event'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit event" wide>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="event-edit-name" className="form-label">
              Event name
            </label>
            <input
              id="event-edit-name"
              type="text"
              className="form-input"
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
                setEditError('');
              }}
              disabled={editSaving}
            />
          </div>
          <div className="form-group">
            <label htmlFor="event-edit-location" className="form-label">
              Event location
            </label>
            <input
              id="event-edit-location"
              type="text"
              className="form-input"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
              disabled={editSaving}
            />
          </div>
          <div className="events-form-row">
            <div className="form-group">
              <label htmlFor="event-edit-start-date" className="form-label">
                Start date
              </label>
              <input
                id="event-edit-start-date"
                type="date"
                className="form-input"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                disabled={editSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="event-edit-end-date" className="form-label">
                End date
              </label>
              <input
                id="event-edit-end-date"
                type="date"
                className="form-input"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                disabled={editSaving}
              />
            </div>
          </div>
          <div className="events-form-row">
            <div className="form-group">
              <label htmlFor="event-edit-start-time" className="form-label">
                Start time
              </label>
              <input
                id="event-edit-start-time"
                type="time"
                className="form-input"
                value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)}
                disabled={editSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="event-edit-end-time" className="form-label">
                End time
              </label>
              <input
                id="event-edit-end-time"
                type="time"
                className="form-input"
                value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)}
                disabled={editSaving}
              />
            </div>
          </div>
          <div className="form-group">
            <span className="form-label">Collaborators</span>
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Collaborators can edit the event and receive event reminder emails.
            </p>
            <TaskAssigneePicker
              users={collaboratorPickerUsers}
              value={editCollabIds}
              onChange={setEditCollabIds}
              addPlaceholder="Search user to add…"
            />
          </div>
          <div className="project-edit-status-toggle-row">
            <button
              type="button"
              className={`project-detail-status-toggle-btn project-detail-status-toggle-btn--${isVoid ? 'open' : 'close'}`}
              disabled={editSaving || statusSaving}
              onClick={() => void submitToggleStatus()}
            >
              {statusSaving
                ? isVoid
                  ? 'Reopening…'
                  : 'Closing…'
                : isVoid
                  ? 'Reopen event'
                  : 'Close event (move to Void)'}
            </button>
          </div>
          {editError ? (
            <p className="create-order-error" role="alert">
              {editError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={editSaving || statusSaving} onClick={() => setEditOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={editSaving || statusSaving} onClick={() => void submitEdit()}>
            {editSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetail;
